import { SortableTableHeader } from '@avanta/shared/components/PaginatedTable/components/SortableTableHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@avanta/shared/components/ui/Table';
import { cn } from '@avanta/shared/utils/cn';
import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type ExpandedState,
  type Row,
  type SortingState,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

interface ExpandableRecordsTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  getRowId: (row: T) => string;
  renderExpandedContent: (row: T) => React.ReactNode;
  initialSorting?: SortingState;
  isLoading?: boolean;
  emptyMessage?: string;
  expandAriaLabel: string;
  collapseAriaLabel: string;
}

export const ExpandableRecordsTable = <T,>({
  data,
  columns,
  getRowId,
  renderExpandedContent,
  initialSorting = [],
  isLoading = false,
  emptyMessage,
  expandAriaLabel,
  collapseAriaLabel,
}: ExpandableRecordsTableProps<T>): React.ReactElement => {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const table = useReactTable({
    data,
    columns,
    state: { sorting, expanded },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getRowId: row => getRowId(row),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  if (isLoading) {
    return <p className="text-denim-800 px-8 py-6 text-sm">{emptyMessage}</p>;
  }

  if (data.length === 0) {
    return <p className="text-denim-800 px-8 py-6 text-sm">{emptyMessage}</p>;
  }

  return (
    <div className="px-4 pb-4 am-lg:px-6">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <TableHead key={header.id}>
                  <SortableTableHeader header={header} />
                </TableHead>
              ))}
              <TableHead className="w-10" />
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map(row => (
            <ExpandableTableRow
              key={row.id}
              row={row}
              renderExpandedContent={renderExpandedContent}
              expandAriaLabel={expandAriaLabel}
              collapseAriaLabel={collapseAriaLabel}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const ExpandableTableRow = <T,>({
  row,
  renderExpandedContent,
  expandAriaLabel,
  collapseAriaLabel,
}: {
  row: Row<T>;
  renderExpandedContent: (row: T) => React.ReactNode;
  expandAriaLabel: string;
  collapseAriaLabel: string;
}): React.ReactElement => {
  const isExpanded = row.getIsExpanded();

  return (
    <>
      <TableRow className={cn(isExpanded && 'bg-beige-50')} onClick={() => row.toggleExpanded()}>
        {row.getVisibleCells().map(cell => (
          <TableCell key={cell.id} className="cursor-pointer">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
        <TableCell className="w-10">
          <button
            type="button"
            className="text-denim-500 hover:text-denim-800 inline-flex size-8 items-center justify-center rounded-sm"
            aria-label={isExpanded ? collapseAriaLabel : expandAriaLabel}
            onClick={event => {
              event.stopPropagation();
              row.toggleExpanded();
            }}
          >
            {isExpanded ? (
              <ChevronUp className="size-4" aria-hidden />
            ) : (
              <ChevronDown className="size-4" aria-hidden />
            )}
          </button>
        </TableCell>
      </TableRow>
      {isExpanded ? (
        <TableRow>
          <TableCell colSpan={row.getVisibleCells().length + 1} className="bg-beige-50 p-0">
            {renderExpandedContent(row.original)}
          </TableCell>
        </TableRow>
      ) : null}
    </>
  );
};
